/**
 * A new catalog row.
 *
 * The slug is the document id, so it is the one thing that cannot be changed afterwards —
 * everything else is editable on the row. It is checked against the same `SLUG_PATTERN`
 * `validateMedium` uses, and against the ids already in the catalog, before the write rather
 * than after: a taken slug would otherwise silently merge into an existing medium.
 *
 * The three other fields are exactly the ones `normalizeMedium` refuses a document without.
 * Nothing else is asked for here, because a create is not the place to fill in a form of
 * forty fields you cannot yet see the shape of.
 */

import { Button, Dialog, Field, SelectField } from '@atlas-nook/design-system';
import { SLUG_PATTERN } from '@atlas-nook/mediums';
import type { MediumFamily } from '@atlas-nook/mediums';
import { useState } from 'react';
import type { JSX } from 'react';

import { createMedium } from '../data/writes.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { entriesOf, FAMILY } from './options.js';

export function NewMediumDialog({
  open,
  taken,
  onClose,
  onCreated,
}: {
  open: boolean;
  taken: Set<string>;
  onClose: () => void;
  onCreated: (id: string) => void;
}): JSX.Element {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [family, setFamily] = useState<MediumFamily>('book');
  const [action, setAction] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [working, setWorking] = useState(false);

  async function create(): Promise<void> {
    const id = slug.trim();
    if (!SLUG_PATTERN.test(id)) {
      setError(S.catalog.slugInvalid);
      return;
    }
    if (taken.has(id)) {
      setError(S.catalog.slugTaken);
      return;
    }

    setError(undefined);
    setWorking(true);
    try {
      await createMedium({
        id,
        name: name.trim() === '' ? id : name.trim(),
        family,
        physicalAction: action.trim(),
      });
      setSlug('');
      setName('');
      setAction('');
      onCreated(id);
    } catch {
      setError(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={S.catalog.newTitle}
      actions={
        <>
          <Button tone="quiet" onClick={onClose}>
            {S.common.cancel}
          </Button>
          <Button tone="primary" working={working} onClick={() => void create()}>
            {S.common.create}
          </Button>
        </>
      }
    >
      <p className="note">{S.catalog.newBody}</p>
      <div className="stack">
        <Field
          label={S.catalog.slug}
          help={S.catalog.slugHelp}
          value={slug}
          {...(error !== undefined ? { error } : {})}
          onChange={setSlug}
        />
        <Field label={S.medium.name} value={name} onChange={setName} />
        <SelectField
          label={S.medium.family}
          value={family}
          onChange={(value) => setFamily(value as MediumFamily)}
        >
          {entriesOf(FAMILY).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
        <Field
          label={S.medium.physicalAction}
          help={S.medium.physicalActionHelp}
          value={action}
          onChange={setAction}
        />
      </div>
    </Dialog>
  );
}
